import {Component} from '@angular/core';
import {SearchForm} from '../../../sdk/component/search/searchForm';
import {Router} from '@angular/router';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {SearchService} from '../../../sdk/component/search/search.service';
import {AlertService} from '../../../sdk/service/alert.service';
import moment from 'moment/moment';
import * as FileSaver from 'file-saver';
import {formatDate} from '@angular/common';
import {
    AccountBatch,
    AccountBatchSortField,
    AccountBatchState,
    MessageCounters,
    Response,
    WebAccountBatchService
} from 'web-angular-client';
import {ROUTES} from '../../../app.routes';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {LayoutService} from '../../layout/layout.service';
import {EnvironmentService} from '../../../sdk/service/environment.service';

@Component({
    selector: 'payment-batch-list',
    templateUrl: 'bulk-payment-request.component.html',
    styleUrls: ['bulk-payment-request.component.scss'],
    standalone: false
})
export class BulkPaymentRequestComponent extends SearchForm
{
    showResendBatchNotificationsDialog: boolean = false;
    sendSMS: boolean = false;
    sendEmail: boolean = false;
    selectedAccountBatch: AccountBatch = null;
    private messageCounters: MessageCounters;

    constructor(
        private router: Router,
        protected layoutService: LayoutService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        private webAccountBatchService: WebAccountBatchService,
        protected searchService: SearchService,
        private ngxLoader: NgxUiLoaderService,
        protected alertService: AlertService)
    {
        super(authenticationService, webAccountBatchService, searchService, environmentService);
    }

    get content(): [AccountBatch]
    {
        return this.results?.content;
    }

    get hasInsufficientEmailCredits(): boolean
    {
        return this.messageCounters?.emailCounter <= 0 || false;
    }

    get hasInsufficientSMSCredits(): boolean
    {
        return this.messageCounters?.smsCounter <= 0 || false;
    }

    create()
    {
        // not used but required to be implemented
    }

    getDefaultSortField(): string
    {
        return AccountBatchSortField.ID;
    }

    getBatchState(accountBatchState: AccountBatchState): string
    {
        switch (accountBatchState)
        {
            case AccountBatchState.QUEUED:
                return 'Queued';
            case AccountBatchState.PROCESSING:
                return 'Processing';
            case AccountBatchState.SENDING:
                return 'Sending';
            case AccountBatchState.COMPLETED_WITH_ERRORS:
                return 'Completed with Errors';
            case AccountBatchState.COMPLETED:
                return 'Completed';
            default:
                return 'Processing';
        }
    }

    getBulkFileTooltipText(): string
    {
        return 'Download a list of request to pay transactions that form part of a batch. The file is in CSV format.';
    }

    getAccountBulkFile(batch: AccountBatch)
    {
        if (this.isGuestAdminUser())
        {
            this.alertService.error('Guest accounts cannot perform this action.', false);
            return;
        }

        this.ngxLoader.start();
        this.webAccountBatchService
            .getBulkFileForAccountBatch(batch.id)
            .subscribe({
                next: (blob: Blob) =>
                {
                    this.ngxLoader.stop();
                    FileSaver.saveAs(
                        blob,
                        this.getAccountBulkFileName(new Date(batch.dateTimeCreated), batch.batchNumber));
                }, error: error =>
                {
                    this.debugOutput(error);
                    this.ngxLoader.stop();
                    this.alertService.error(
                        'Failed to retrieve bulk file for the selected account batch. Please try again later.');
                }
            });
    }

    cancel(batch: AccountBatch)
    {
        if (this.isGuestAdminUser())
        {
            this.alertService.error('Guest accounts cannot perform this action.', false);
            return;
        }

        this.ngxLoader.start();
        this.webAccountBatchService
            .cancelAccountBatch(batch.id)
            .subscribe({
                next: (batchCancelList) =>
                {
                    this.ngxLoader.stop();
                    const total: number = batchCancelList.length;
                    const success: number = batchCancelList.filter(a => a.success).length;

                    if (total !== success)
                    {
                        this.alertService.info(
                            `${success}/${total} outstanding request to pay transactions were cancelled successfully.\n 
                      ${total - success} outstanding request to pay transactions failed to cancel.`);
                    }
                    else
                    {
                        this.alertService.success(
                            `${success} of ${total} outstanding request to pay transactions were cancelled successfully.`
                        );
                    }
                }, error: error =>
                {
                    this.ngxLoader.stop();
                    this.debugOutput(error);
                    if (error.status === 404)
                    {
                        this.alertService.info(
                            'No outstanding request to pay transactions found in the batch');
                    }
                    else
                    {
                        this.alertService.error(
                            'Failed to cancel the batch. Please try again later.');
                    }
                }
            });
    }

    downloadUploadErrors(batch: AccountBatch): void
    {
        if (this.isGuestAdminUser())
        {
            this.alertService.error('Guest accounts cannot perform this action.', false);
            return;
        }

        this.ngxLoader.start();
        this.webAccountBatchService
            .getUploadErrorFile(batch.id)
            .subscribe({
                next: (blob: Blob) =>
                {
                    this.ngxLoader.stop();
                    FileSaver.saveAs(blob, `Batch-${batch.batchNumber} Upload Error File.csv`);
                }, error: (error: any) =>
                {
                    this.ngxLoader.stop();
                    this.debugOutput(error);
                    this.alertService.error('Failed to retrieve upload error file. Please try again later.');
                }
            });
    }

    view(batch: AccountBatch)
    {
        this.router.navigate(
            [ROUTES.SECURE.ACCOUNT.LIST.path],
            {queryParams: {batchNumber: batch.batchNumber}}).then();
    }

    openResendBatchNotificationsDialog(batch: AccountBatch)
    {
        this.selectedAccountBatch = batch;
        this.showResendBatchNotificationsDialog = true;
    }

    closeResendBatchNotificationsDialog()
    {
        this.selectedAccountBatch = null;
        this.showResendBatchNotificationsDialog = false;
        this.sendSMS = false;
        this.sendEmail = false;
    }

    resendBatchNotifications()
    {
        let batch = this.selectedAccountBatch;
        let sendEmail = this.sendEmail;
        let sendSMS = this.sendSMS;
        this.closeResendBatchNotificationsDialog();

        this.ngxLoader.start();
        this.webAccountBatchService.resendUnsentAccountBatchNotifications(batch?.id, sendEmail, sendSMS).subscribe({
            next: (response: Response) =>
            {
                this.ngxLoader.stop();
                if (response.responseCode == 'true')
                {
                    this.alertService.info(response.responseMessage + '. Please refresh the page to see the latest batch status.');
                }
                else
                {
                    this.alertService.error(response.responseMessage);
                }
            }, error: () =>
            {
                this.ngxLoader.stop();
                this.alertService.error(`Failed to resend notifications for batch number ${batch?.batchNumber}. Please try again.`);
            }
        });
    }

    isSendEmailText(): string
    {
        return this.sendEmail ? 'YES' : 'NO';
    }

    isSendSMSText(): string
    {
        return this.sendSMS ? 'YES' : 'NO';
    }

    protected getAccountBulkFileName(batchDateCreated: Date, batchNumber: number): string
    {
        // use the batch closed timestamp if available, otherwise fallback to the current date-time
        const fileTimestamp = batchDateCreated !== null ? moment(batchDateCreated).local().toDate() : new Date();
        return `${formatDate(fileTimestamp, 'yyyyMMdd', 'en')}_accountBulk_${batchNumber}.csv`;
    }
}